export const DOCUMENT_SERVICE_STATUSES = {
  payment: {
    pending: { label: 'Pending', tone: 'amber' },
    processing: { label: 'Processing', tone: 'amber' },
    paid: { label: 'Paid', tone: 'emerald' },
    failed: { label: 'Failed', tone: 'red' },
    cancelled: { label: 'Cancelled', tone: 'slate' },
    partially_refunded: { label: 'Partially refunded', tone: 'orange' },
    refunded: { label: 'Refunded', tone: 'slate' },
    review_required: { label: 'Review required', tone: 'red' },
  },
  order: {
    draft: { label: 'Draft', tone: 'slate' },
    pending_payment: { label: 'Pending payment', tone: 'amber' },
    paid: { label: 'Paid', tone: 'emerald' },
    processing: { label: 'Processing', tone: 'gold' },
    completed: { label: 'Completed', tone: 'emerald' },
    cancelled: { label: 'Cancelled', tone: 'slate' },
    refunded: { label: 'Refunded', tone: 'slate' },
  },
  fulfillment: {
    not_started: { label: 'Not started', tone: 'slate' },
    verification_required: { label: 'Verification required', tone: 'amber' },
    verifying_records: { label: 'Verifying records', tone: 'amber' },
    approved_for_processing: { label: 'Approved for processing', tone: 'gold' },
    preparing: { label: 'Preparing', tone: 'gold' },
    ready_for_pickup: { label: 'Ready for pickup', tone: 'emerald' },
    shipped: { label: 'Shipped', tone: 'emerald' },
    delivered: { label: 'Delivered', tone: 'emerald' },
    completed: { label: 'Completed', tone: 'emerald' },
    unable_to_fulfill: { label: 'Unable to fulfill', tone: 'red' },
  },
  accounting: {
    pending: { label: 'Pending', tone: 'amber' },
    synced: { label: 'Synced', tone: 'emerald' },
    retrying: { label: 'Retrying', tone: 'orange' },
    attention_required: { label: 'Attention required', tone: 'red' },
  },
};

export const ORDER_STATUS_OPTIONS = Object.keys(DOCUMENT_SERVICE_STATUSES.order);
export const FULFILLMENT_STATUS_OPTIONS = Object.keys(DOCUMENT_SERVICE_STATUSES.fulfillment);

export function asArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  return [];
}

export function apiItems(data, keys = []) {
  if (Array.isArray(data)) return data;
  for (const key of ['items', ...keys]) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
}

export function apiTotal(data, items = []) {
  const total = Number(data?.total ?? data?.count);
  return Number.isFinite(total) ? total : items.length;
}

export function unwrapEntity(data, keys = []) {
  if (!data || typeof data !== 'object') return data;
  for (const key of keys) {
    if (data[key] && typeof data[key] === 'object') return data[key];
  }
  return data;
}

export function formatCad(cents, fallback = 'C$0.00') {
  const integer = Number(cents);
  if (!Number.isFinite(integer)) return fallback;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Math.trunc(integer) / 100);
}

export function formatServiceDate(value, includeTime = false) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  }).format(date);
}

export function humanize(value, fallback = '—') {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value).replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function statusMeta(kind, status) {
  return DOCUMENT_SERVICE_STATUSES[kind]?.[status]
    || { label: humanize(status, 'Unknown'), tone: 'neutral' };
}

/**
 * The buyer-facing option sections, in the order staff arranged them.
 *
 * The server sends these already priced, each value carrying what it *adds*
 * rather than what the whole combination costs. That is deliberate: two lists
 * of absolute prices side by side read as though they stack, which is how a
 * $45.00 order came to look like $75.00.
 */
export function optionSections(product) {
  return asArray(product?.option_sections);
}

export function productPricing(product) {
  return product?.pricing || {};
}

/** Wording staff control from the product editor. */
export function productPresentation(product) {
  return {
    summary_heading: 'Order details',
    quantity_section_label: 'Choose your transcripts',
    quantity_field_label: 'Number of copies',
    show_product_code: false,
    show_variant_sku: false,
    show_tax_note: false,
    tax_note: '',
    ...(product?.presentation || {}),
  };
}

/**
 * What one option adds, as a label.
 *
 * Falls back to the absolute price when the catalog's price matrix is not
 * additively separable — an administrator can always price one combination
 * oddly, and an uplift that is wrong for some combinations is worse than no
 * uplift at all. The server decides which mode applies.
 */
export function optionPriceLabel(value, displayMode) {
  if (!value) return '';
  return displayMode === 'absolute' ? value.price_label : value.uplift_label;
}

/** The variant matching one selection across every dimension. */
export function findVariant(product, selection) {
  const entries = Object.entries(selection || {});
  return productVariants(product).find((variant) => {
    const options = variantOptions(variant);
    return entries.every(([key, value]) => options[key] === value);
  }) || null;
}

/** The cheapest option in each section — the selection a page opens on. */
export function defaultSelection(product) {
  const selection = {};
  optionSections(product).forEach((section) => {
    const available = asArray(section.values).filter((value) => value.available !== false);
    const cheapest = available.reduce(
      (best, value) => (best === null || value.uplift_cents < best.uplift_cents ? value : best),
      null,
    );
    if (cheapest) selection[section.id] = cheapest.value;
  });
  return selection;
}

/**
 * The order summary, recomputed as the buyer clicks.
 *
 * This mirrors the server's breakdown so the page updates without a request
 * per click. It is a *preview* only: the amount actually charged is always
 * recomputed server-side when the order is created, from the stored variant
 * price rather than from anything the browser sends.
 */
export function buildBreakdown(product, selection, quantity = 1) {
  const pricing = productPricing(product);
  const sections = optionSections(product);
  const variant = findVariant(product, selection);
  const copies = Math.max(1, Number(quantity) || 1);
  const base = Number(pricing.base_price_cents || 0);
  const extraCopy = Number(pricing.additional_copy_price_cents ?? base);
  const name = product?.name || 'Document';

  const lines = [{
    kind: 'item',
    label: copies > 1 ? `${copies} × ${name}` : name,
    detail: copies > 1 ? `${formatCad(base)} each` : '',
    amount_cents: base * copies,
    amount_label: formatCad(base * copies),
  }];

  sections.forEach((section) => {
    const chosen = asArray(section.values).find((value) => value.value === selection?.[section.id]);
    if (!chosen) return;
    lines.push({
      kind: 'option',
      dimension_id: section.id,
      // The choice first, because that is what the buyer recognises; the
      // category in brackets so they can still tell which question it answered.
      label: `${chosen.label} (${String(section.label || '').toLowerCase()})`,
      detail: '',
      amount_cents: chosen.uplift_cents,
      amount_label: chosen.uplift_label,
    });
  });

  const variantPrice = variant ? variantPriceCents(variant) : null;
  const total = variantPrice === null
    ? lines.reduce((sum, line) => sum + line.amount_cents, 0)
    : variantPrice + (copies - 1) * extraCopy;

  // Only reachable when the price matrix is not additively separable, where the
  // uplifts are lower bounds. Reconciling openly beats showing lines that do
  // not add up to the amount charged.
  const accounted = lines.reduce((sum, line) => sum + line.amount_cents, 0);
  if (accounted !== total) {
    const difference = total - accounted;
    lines.push({
      kind: 'adjustment',
      label: 'Selected combination',
      detail: '',
      amount_cents: difference,
      amount_label: difference === 0 ? 'Included' : `${difference > 0 ? '+' : ''}${formatCad(difference)}`,
    });
  }

  return { lines, quantity: copies, total_cents: total, variant };
}

export function productVariants(product) {
  const variants = asArray(product?.variants || product?.visible_variants);
  return variants.filter((variant) => (
    variant?.active !== false
    && variant?.enabled !== false
    && !['archived', 'disabled', 'unavailable'].includes(String(variant?.status || '').toLowerCase())
  ));
}

export function variantId(variant) {
  return variant?.id || variant?.uuid || variant?.variant_id || variant?.sku || '';
}

export function variantPriceCents(variant) {
  return Number(variant?.price_cents ?? variant?.amount_cents ?? variant?.unit_price_cents ?? 0) || 0;
}

export function variantOptions(variant) {
  const raw = variant?.options || variant?.option_values || variant?.dimensions || {};
  if (Array.isArray(raw)) {
    return raw.reduce((acc, item) => {
      if (item?.name || item?.key) acc[item.name || item.key] = item.value;
      return acc;
    }, {});
  }
  return raw && typeof raw === 'object' ? raw : {};
}

export function variantLabel(variant) {
  if (variant?.name || variant?.label || variant?.title) return variant.name || variant.label || variant.title;
  const parts = Object.values(variantOptions(variant)).filter(Boolean).map((value) => humanize(value));
  return parts.length ? parts.join(' · ') : variant?.sku || 'Service option';
}

export function productFormFields(product) {
  const schema = product?.form_schema || product?.form || product?.schema
    || product?.current_form_version?.schema || product?.form_version?.schema || {};
  return asArray(schema?.fields || schema);
}

export function fieldId(field, index = 0) {
  return String(field?.id || field?.key || field?.name || `field_${index}`);
}

export function prefillServiceAnswers(fields, safePrefill) {
  if (!safePrefill || typeof safePrefill !== 'object' || Array.isArray(safePrefill)) return {};
  return asArray(fields).reduce((answers, field, index) => {
    const id = fieldId(field, index);
    const sourceKey = String(field?.prefill_key || id);
    if (Object.prototype.hasOwnProperty.call(safePrefill, id)) answers[id] = safePrefill[id];
    else if (Object.prototype.hasOwnProperty.call(safePrefill, sourceKey)) answers[id] = safePrefill[sourceKey];
    return answers;
  }, {});
}

export function serviceAnswersWithDefaults(fields, answers = {}, variant = null) {
  let next = answers && typeof answers === 'object' && !Array.isArray(answers) ? answers : {};
  asArray(fields).forEach((field, index) => {
    if (String(field?.type || '').toLowerCase() !== 'address' || !field?.required) return;
    if (!isFieldVisible(field, next, variant)) return;
    const id = fieldId(field, index);
    const current = next[id] && typeof next[id] === 'object' && !Array.isArray(next[id]) ? next[id] : {};
    if (Object.prototype.hasOwnProperty.call(current, 'country')) return;
    const country = asArray(field?.address_fields).find((item) => item?.key === 'country');
    if (next === answers) next = { ...next };
    next[id] = { ...current, country: country?.default || 'Canada' };
  });
  return next;
}

function expectedMatches(actual, expected, operator = 'equals') {
  if (operator === 'in') return asArray(expected).map(String).includes(String(actual));
  if (operator === 'not_equals' || operator === 'neq') return String(actual) !== String(expected);
  if (operator === 'truthy') return Boolean(actual);
  if (operator === 'falsy') return !actual;
  return String(actual ?? '') === String(expected ?? '');
}

export function isFieldVisible(field, answers = {}, variant = null) {
  const condition = field?.show_when || field?.visible_when || field?.condition;
  if (!condition) return true;
  const conditions = Array.isArray(condition) ? condition : condition?.all || [condition];
  const options = variantOptions(variant);
  return conditions.every((item) => {
    const key = item?.field || item?.field_id || item?.key || item?.dimension;
    if (!key) return true;
    const actual = Object.prototype.hasOwnProperty.call(answers, key) ? answers[key] : options[key];
    const expected = item?.value ?? item?.equals ?? item?.one_of;
    return expectedMatches(actual, expected, item?.operator || (item?.one_of ? 'in' : 'equals'));
  });
}

export function normalizeFieldOptions(options) {
  return asArray(options).map((option) => (
    typeof option === 'string'
      ? { value: option, label: humanize(option) }
      : { value: String(option?.value ?? option?.id ?? ''), label: option?.label || option?.name || humanize(option?.value) }
  )).filter((option) => option.value);
}

export function validateServiceAnswers(fields, answers, variant) {
  const errors = {};
  asArray(fields).forEach((field, index) => {
    if (!isFieldVisible(field, answers, variant)) return;
    const id = fieldId(field, index);
    const value = answers?.[id];
    const conditionallyRequired = field?.required_when
      ? isFieldVisible({ visible_when: field.required_when }, answers, variant)
      : false;
    const required = Boolean(field?.required || conditionallyRequired);
    const empty = value === undefined || value === null || value === '' || value === false
      || (Array.isArray(value) && value.length === 0)
      || (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);
    if (required && empty) {
      errors[id] = `${field.label || field.title || humanize(id)} is required.`;
      return;
    }
    const type = String(field?.type || field?.input_type || 'text').toLowerCase();
    if (type === 'address' && value && typeof value === 'object') {
      const address = {
        address_1: value.address_1 || value.line1 || value.address_line_1,
        city: value.city,
        province: value.province || value.region || value.province_state,
        postal_code: value.postal_code,
        country: value.country,
      };
      const missing = Object.entries(address).filter(([, part]) => !String(part || '').trim()).map(([name]) => name);
      if (required && missing.length) {
        errors[id] = 'Enter a complete address, including street, city, province or state, postal code, and country.';
      }
      return;
    }
    if (value && (type === 'email' || field?.format === 'email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) {
      errors[id] = 'Enter a valid email address.';
      return;
    }
    if (typeof value === 'string') {
      const minimum = Number(field?.min_length);
      const maximum = Number(field?.max_length);
      if (Number.isFinite(minimum) && value.trim().length < minimum) {
        errors[id] = `Enter at least ${minimum} characters.`;
        return;
      }
      if (Number.isFinite(maximum) && value.trim().length > maximum) {
        errors[id] = `Enter no more than ${maximum} characters.`;
        return;
      }
      if (field?.pattern) {
        try {
          if (!new RegExp(`^(?:${field.pattern})$`).test(value)) {
            errors[id] = 'Enter a value in the required format.';
            return;
          }
        } catch { /* The backend rejects invalid schema patterns. */ }
      }
    }
    if (Object.prototype.hasOwnProperty.call(field || {}, 'accepted_value') && value !== field.accepted_value) {
      errors[id] = 'You must accept this requirement to continue.';
    }
  });
  return errors;
}

export function orderId(order) {
  return order?.id || order?.uuid || order?.order_id || '';
}

export function orderNumber(order) {
  return order?.order_number || order?.friendly_id || order?.number || 'Order';
}

export function orderItems(order) {
  return asArray(order?.items || order?.order_items);
}

export function orderTotalCents(order) {
  return Number(order?.total_cents ?? order?.amount_cents ?? order?.total_amount_cents ?? 0) || 0;
}

export function permissionsFrom(data) {
  return data?.permissions || data?.meta?.permissions || {};
}

export function hasPermission(permissions, name, defaultValue = false) {
  if (!permissions || typeof permissions !== 'object') return defaultValue;
  return permissions[name] === true;
}

export function orderingEnabled(data, defaultValue = true) {
  const value = data?.enabled ?? data?.ordering_enabled ?? data?.student_ordering_enabled
    ?? data?.config?.ordering_enabled ?? data?.config?.student_ordering_enabled;
  return value === undefined ? defaultValue : value === true;
}

export function apiErrorMessage(error, fallback = 'Something went wrong.') {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  if (typeof error?.response?.data?.message === 'string') return error.response.data.message;
  return fallback;
}

export function normalizeNotes(notes) {
  return asArray(notes).map((note) => ({
    ...note,
    id: note.id || note.uuid,
    text: note.text || note.body || '',
    author: note.author || note.actor_name || note.created_by_name || 'Staff member',
    createdAt: note.createdAt || note.created_at,
    editedAt: note.editedAt || note.edited_at,
    parentId: note.parentId || note.parent_id || null,
  }));
}
